/* global window */

const { computed } = require('mobx');
const { inject, observer } = require('mobx-react');
const { Link, withRouter } = require('react-router-dom');
const React = require('react');

const { AbacusControl, AbacusExperiment, AbacusVariant } = require('bernie-abacus');
const { ErrorBoundary, ViewLarge, Viewport, ViewSmall } = require('bernie-view');
const { getBexApiContext } = require('bernie-context');
const { LocalizedText, withLocalization } = require('bernie-l10n');
const { QueryRoute } = require('bernie-core');
const { withAnalytics } = require('bernie-analytics');

const { ActivityRecommendations } = require('@shared-ui/local-expert-activities');

const { PageName } = require('constants/pages');

const { DateUtils } = require('utils/date-utils');
const { mergeProductStrings } = require('utils/infosite-utils');

// Components
const { ChatBot } = require('components/common/chat-bot');
const { BentoBox } = require('bento/bento-box.tsx');
const { BentoPortion } = require('bento/bento-portion.tsx');
const { BentoSideDish } = require('bento/bento-side-dish.tsx');
const { DatelessFaqs } = require('components/common/faq/dateless-faqs');
const { FavoriteButton } = require('components/common/shortlist/favorite-button');
const { FeedbackButton } = require('components/infosite/feedback/feedback-button');
const { GalleryByCategories } = require('components/infosite/property-details/gallery-by-categories');
const { Lazy } = require('components/common/lazy/lazy');
const { MainRegion } = require('components/layout/pages/main-region');
const { MapAccess } = require('components/infosite/property-details/map-access');
const { MasterPage } = require('components/layout/pages/master-page');
const { MesoFooter } = require('components/meso/meso-footer');
const { MesoIntentMediaExp } = require('components/meso/meso-intent-media-exp');
const { MesoSideRail } = require('components/meso/meso-side-rail');
const { MessagingCard } = require('components/common/messaging-card/messaging-card');
const { MultiItem } = require('components/multi-item');
const { NavigationBar } = require('components/infosite/navigation/navigation-bar');
const { NavigationRefs } = require('components/infosite/navigation/navigation-refs');
const { NavigationRegion } = require('components/layout/pages/navigation-region');
const { NoReviews } = require('components/infosite/property-details/no-reviews');
const { PropertyDetails } = require('components/infosite/property-details');
const { PropertyInformation } = require('components/infosite/property-information');
const { PropertySummary } = require('components/infosite/property-summary');
const { ReviewsOnInfosite } = require('components/infosite/property-details/reviews-on-infosite');
const { RewardsEducationDialog } = require('components/common/rewards-education-dialog');
const { RoninPropertyDetails } = require('components/infosite/property-details/ronin-property-details');
const { roomsAndRatesLoadable } = require('components/infosite/rooms-and-rates/loadable');
const { SchemaOrgScript } = require('components/common/schema-org-script/schema-org-script');
const { SideRailRegion } = require('components/layout/pages/side-rail-region');
const { SimilarHotels } = require('components/infosite/similar-hotels/similar-hotels');
const { SleepingArrangements } = require('components/infosite/property-sleeping-arrangements');
const { WelcomeNotification } = require('components/common/welcome-notification');
const { WhatsAroundCard } = require('components/infosite/whats-around/whats-around-card');

const MasterPageWrapped = withAnalytics(MasterPage);
const MasterPageSideRailTypes = MasterPage.wrappedComponent.sideRailTypes;
const MasterPageToolbarTypes = MasterPage.wrappedComponent.toolbarTypes;

@withLocalization
@withRouter
@inject('context', 'searchResults', 'currentHotel', 'page', 'uiPage', 'analytics', 'routeStore', 'layerManager', 'multiItem')
@observer
class Infosite extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      forceHistoryPush: false,
      roomsAndRatesTarget: undefined,
      contextInput: props.context && props.context.site && getBexApiContext(props.context)
    };

    this.pageShowEvents = this.pageShowEvents.bind(this);
    this.goBack = this.goBack.bind(this);
    this.forceHistoryPushOnGoBack = this.forceHistoryPushOnGoBack.bind(this);

    if (props.page && props.page.setPageName) {
      props.page.setPageName(PageName.INFOSITE);
    }

    this.setRoomsAndRatesTarget = this.setRoomsAndRatesTarget.bind(this);

    if (
      this.props.multiItem &&
      this.props.multiItem.isMixMatchEnabled &&
      typeof this.props.multiItem.getOrCreateCart === 'function' &&
      !this.props.multiItem.isPackageShoppingPath()
    ) {
      this.props.multiItem.getOrCreateCart(this.props.context);
    }
  }

  @computed
  get omnitureData() {
    if (this.props.currentHotel && this.props.analytics && !this.props.currentHotel.loadingOffers) {
      const hotelData = this.props.currentHotel;
      const infoOmnitureData = (hotelData.hotel && hotelData.hotel.omnitureData) || null;
      const offersOmnitureData = (hotelData.offers && hotelData.offers.omnitureData) || null;

      if (infoOmnitureData && offersOmnitureData) {
        const infoOmnitureCopy = JSON.parse(JSON.stringify(infoOmnitureData));
        const offersOmnitureCopy = JSON.parse(JSON.stringify(offersOmnitureData));
        mergeProductStrings(infoOmnitureCopy, offersOmnitureCopy);
        if (this.props.currentHotel.isHotelInfoCached) {
          infoOmnitureCopy.traceId = offersOmnitureCopy.omnitureProperties.traceId;
        }

        return this.props.analytics.mergeOmnitureData(infoOmnitureCopy, offersOmnitureCopy);
      }
    }

    return null;
  }

  @computed.struct
  get universalDataObject() {
    if (this.props.currentHotel && this.props.currentHotel.hotel && typeof this.props.currentHotel.hotel.universalDataObject === 'object') {
      return this.props.currentHotel.hotel.universalDataObject;
    }

    return {};
  }

  componentDidMount() {
    const { analytics } = this.props;
    analytics.markPageUsable();
    // Mobile safari uses the back-forward cache meaning when a user clicks back
    // from CKO to HIS the spinner is still showing and the components do not re render,
    // need to manually dismiss this from a previously attached event.
    window.addEventListener('pageshow', this.pageShowEvents);
    window.scrollTo(0, 0);
  }

  componentDidUpdate(prevProps) {
    const { layerManager, routeStore, uiPage, location, history } = this.props;

    const layer = layerManager && layerManager.length;

    if (!layer && routeStore.queryParams.scrollPosition) {
      uiPage.setFocusItem(routeStore.queryParams.scrollPosition);
      const query = routeStore.mergeQueryString(routeStore.search, null, key => !key.startsWith('scrollPosition'));
      const queryCombinerFunction = (prevQuery, newQuery) => newQuery;
      routeStore.updateSearch(query, undefined, true, queryCombinerFunction);
    } else if (location && prevProps.location && location.pathname !== prevProps.location.pathname && history.action === 'PUSH') {
      window.scrollTo(0, 0);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.pageShowEvents);
  }

  pageShowEvents() {
    this.props.uiPage.hideProgressIndicator();
  }

  setRoomsAndRatesTarget(roomsAndRatesTarget) {
    this.setState({
      roomsAndRatesTarget
    });
  }

  forceHistoryPushOnGoBack() {
    this.props.searchResults.clearDefaultRequestFields();
    this.setState({
      forceHistoryPush: true
    });
  }

  goBack(e) {
    const { analytics, routeStore, history, currentHotel, l10n } = this.props;
    const infositeContext = currentHotel && currentHotel.offers && currentHotel.offers.infositeContext;
    const backToHsrUrl = infositeContext && infositeContext.backToHsrUrl;

    if (analytics) {
      analytics.trackEvent('.BackToSearch', l10n.formatText('hotels.infosite.backToSearchResults.linkText'), {});
    }

    if (this.state.forceHistoryPush || routeStore.navigationDepth <= 0) {
      history.push(backToHsrUrl);
    } else {
      e.preventDefault();
      routeStore.goBack();
    }
  }

  getCurrentHotelInfo(currentHotel) {
    let info = {};

    if (currentHotel && currentHotel.hotel) {
      info = {
        ...currentHotel.hotel
      };
    }

    if (currentHotel && currentHotel.reviews) {
      info.reviews = currentHotel.reviews;
    }

    return info;
  }

  renderLayout() {
    const { currentHotel, analytics, page, l10n, route, routeStore, context, multiItem } = this.props;
    const hotelRegion = currentHotel && currentHotel.hotel && currentHotel.hotel.region;
    const searchData = currentHotel && currentHotel.offerSearchData && currentHotel.offerSearchData;

    const { searchCriteria } = currentHotel;
    const isFailure = currentHotel && currentHotel.isFailure;
    const loadingOffers = currentHotel && currentHotel.loadingOffers;
    const toolbar = (currentHotel && currentHotel.toolbar) || {};
    const info = this.getCurrentHotelInfo(currentHotel);
    const offers = currentHotel && currentHotel.offers;
    const stickyOffer = offers && offers.stickyOffer;
    const priceGuarantee = offers && offers.propertyInfo && offers.propertyInfo.priceGuarantee;
    const similarHotels = currentHotel && currentHotel.similarHotels;
    const similarHotelsDateless = currentHotel && currentHotel.similarHotelsDateless;
    const destination = searchCriteria && searchCriteria.destination;
    const infositeContext = offers && offers.infositeContext;
    const backToHsrUrl = infositeContext && infositeContext.backToHsrUrl;
    const membershipMessagingCardViewModel = currentHotel && currentHotel.messagingCardModel && currentHotel.messagingCardModel.model;
    const freeLxMessagingCardViewModel =
      currentHotel && currentHotel.offers && currentHotel.offers.freeLxMessagingCard && currentHotel.offers.freeLxMessagingCard.model;
    const { roomsAndRatesTarget } = this.state;
    const rewardsEducation =
      currentHotel &&
      currentHotel.offers &&
      currentHotel.offers.rewardsDialogContent &&
      currentHotel.offers.rewardsDialogContent.rewardsEducation;
    const hrefNextPage = page && page.hrefNextPage;
    const questionAnswersViewModel = currentHotel.hotel && currentHotel.hotel.questionAnswersViewModel;
    const { reviews } = currentHotel;
    const feedbackModel = currentHotel && currentHotel.hotel && currentHotel.hotel.feedbackModel;
    const vrInquiryChatbotViewModel = currentHotel.hotel && currentHotel.hotel.vrInquiryChatbotViewModel;
    if (!this.props.page.seo) {
      this.props.page.title = `${info && info.hotelName} ${destination ? `(${destination})` : ''}`;
    }
    const hotelId = currentHotel && currentHotel.hotelId;
    const shortlistModel = currentHotel && currentHotel.hotel && currentHotel.hotel.shortlistModel;
    const favoriteButton = shortlistModel &&
      shortlistModel.meta &&
      shortlistModel.meta.hotelId && (
        <FavoriteButton
          shortlistModel={shortlistModel}
          labelSave={l10n.formatText('hotels.infosite.shortlist.saveToList.a11yLabel')}
          labelRemove={l10n.formatText('hotels.infosite.shortlist.removeFromList.a11yLabel')}
          category="hotel"
          onSaveMessage={l10n.formatText('hotels.shortlist.saveToList.notification', info.hotelName)}
          id={`favorite-${hotelId}`}
          {...info}
        />
      );
    const masterProps = {
      className: 'infosite',
      dataStid: 'infosite-page-container',
      loading: !this.omnitureData,
      omnitureData: this.omnitureData,
      pageUsable: true,
      route: route,
      searchCriteria: currentHotel.searchCriteria,
      universalDataObject: this.universalDataObject
    };
    const isDateless = searchCriteria && searchCriteria.isDateless;

    const PropertySummaryComponent = (
      <PropertySummary
        {...info}
        backToHsrUrl={backToHsrUrl}
        name={toolbar.name}
        goBack={this.goBack}
        favoriteButton={favoriteButton}
        currentHotel={currentHotel}
        roomsAndRatesTarget={roomsAndRatesTarget}
        stickyOffer={stickyOffer}
      />
    );

    const structuredData = context && context.viewModelResponse && context.viewModelResponse.structuredData;
    const propertyFaqs = currentHotel.hotel && currentHotel.hotel.propertyFaqs;

    return (
      <MasterPageWrapper {...masterProps}>
        <NavigationRegion className="navigation-bar-region constrained">
          {PropertySummaryComponent}
          {info && info.navigationBarTabList && <NavigationBar navigationBarTabList={info.navigationBarTabList} />}
        </NavigationRegion>
        <MainRegion key="main-region" className="main-region infosite__main">
          {isFailure && (
            <p className="error" data-stid="infosite-fetch-error">
              <LocalizedText message="hotels.infosite.errorMessage.text" />
              <Link to={'/Hotels'}>
                <LocalizedText message="hotels.infosite.tryAgain.text" />
              </Link>
            </p>
          )}
          <div ref={NavigationRefs.Overview} className="infosite__content infosite__content--details">
            <PropertyDetails
              {...info}
              goBack={this.goBack}
              index={0}
              priceGuarantee={priceGuarantee}
              roomsAndRatesTarget={roomsAndRatesTarget}
              stickyOffer={stickyOffer}
              favoriteButton={favoriteButton}
            />
            {currentHotel.messagingCardModel && (
              <ErrorBoundary label="hotel.information.membership">
                <div className="mod-signup-signin-banner">
                  <MessagingCard
                    className="all-b-padding-three all-x-padding-six xl-x-padding-zero m-x-padding-zero s-x-padding-two"
                    model={membershipMessagingCardViewModel}
                    routeStore={routeStore}
                    tag="section"
                  />
                </div>
              </ErrorBoundary>
            )}
            {freeLxMessagingCardViewModel && (
              <ErrorBoundary label="hotel.information.freeLx">
                <div id="free-lx-banner">
                  <MessagingCard
                    className="all-b-padding-three all-x-padding-six xl-x-padding-zero m-x-padding-zero s-x-padding-two"
                    model={freeLxMessagingCardViewModel}
                    routeStore={routeStore}
                    tag="section"
                  />
                </div>
              </ErrorBoundary>
            )}
            {React.createElement(roomsAndRatesLoadable.codeSplit, {
              ...offers,
              forceHistoryPushOnGoBack: this.forceHistoryPushOnGoBack,
              infoModel: info,
              loadingOffers,
              onRoomsAndRatesTargetAvailable: this.setRoomsAndRatesTarget
            })}
            <SleepingArrangementsModule analytics={analytics} l10n={l10n} offers={offers} />
            {vrInquiryChatbotViewModel && (
              <ErrorBoundary label="hotel.information.chatbot">
                <ChatBot model={vrInquiryChatbotViewModel} />
              </ErrorBoundary>
            )}
            <ErrorBoundary label="hotel.similarHotels">
              <SimilarHotels similarHotels={similarHotels} analytics={analytics} />
            </ErrorBoundary>

            <ErrorBoundary label="hotel.information.whatsAround_1">
              <>{info && <WhatsAroundCard mapModel={info.mapModel} />}</>
            </ErrorBoundary>

            <ErrorBoundary label="hotel.information.similarPropertiesDateless">
              <SimilarHotels similarHotels={similarHotelsDateless} analytics={analytics} isDatelessPdp={isDateless} />
            </ErrorBoundary>

            <ErrorBoundary label="hotel.information.activities">
              <>
                {this.state.contextInput &&
                  hotelRegion &&
                  hotelRegion.id &&
                  searchData &&
                  searchData.startDate &&
                  searchData.endDate && (
                    <Lazy>
                      <Lazy.Placeholder>
                        <div data-stid="recommended-activities__placeholder" />
                      </Lazy.Placeholder>
                      <Lazy.Loaded>
                        <AbacusExperiment name="LS_PWA_RECOMMENDED_ACTIVITIES">
                          <AbacusControl />
                          <AbacusVariant bucket={1}>
                            <ActivityRecommendations
                              inputs={{
                                context: this.state.contextInput,
                                region: {
                                  regionId: String(hotelRegion.id)
                                },
                                dateRange: {
                                  startDate: DateUtils.parseDateString(searchData.startDate),
                                  endDate: DateUtils.parseDateString(searchData.endDate)
                                }
                              }}
                              skipSsr={true}
                            />
                          </AbacusVariant>
                        </AbacusExperiment>
                      </Lazy.Loaded>
                    </Lazy>
                  )}
              </>
            </ErrorBoundary>

            <PropertyInformation
              {...info}
              {...offers}
              analytics={this.props.analytics}
              hrefNextPage={hrefNextPage}
              questionAnswersViewModel={questionAnswersViewModel}
            />
            <DatelessFaqs isDateless={isDateless} propertyFaqs={propertyFaqs} />
            {reviews && reviews.userReviews && reviews.userReviews.length > 0 && <ReviewsOnInfosite currentHotel={currentHotel} />}
            {feedbackModel && (
              <ErrorBoundary label="hotel.information.feedback">
                <FeedbackButton model={feedbackModel} />
              </ErrorBoundary>
            )}
          </div>
          <MesoFooter pageName={PageName.INFOSITE} destination={destination} isDateless={isDateless} />

          <MesoIntentMediaExp pageName={PageName.INFOSITE} searchCriteria={searchCriteria} />
          <QueryRoute query={{ pwaDialog: 'rewardsEducation' }}>
            <RewardsEducationDialog rewardsEducation={rewardsEducation} />
          </QueryRoute>
          <WelcomeNotification />
          <SchemaOrgScript structuredData={structuredData} />
          {multiItem && !multiItem.isPackageShoppingPath() && <MultiItem analytics={analytics} pageName={PageName.INFOSITE} />}
        </MainRegion>
        <SideRailRegion key="side-rail-region">
          <MesoSideRail pageName={PageName.INFOSITE} />
        </SideRailRegion>
      </MasterPageWrapper>
    );
  }

  renderLayoutWithSdui() {
    const { currentHotel, analytics, page, l10n, route, routeStore, context, multiItem } = this.props;
    const hotelRegion = currentHotel && currentHotel.hotel && currentHotel.hotel.region;
    const searchData = currentHotel && currentHotel.offerSearchData && currentHotel.offerSearchData;

    const { searchCriteria } = currentHotel;

    const isFailure = currentHotel && currentHotel.isFailure;
    const loadingOffers = currentHotel && currentHotel.loadingOffers;
    const toolbar = (currentHotel && currentHotel.toolbar) || {};
    const info = this.getCurrentHotelInfo(currentHotel);
    const offers = currentHotel && currentHotel.offers;
    const stickyOffer = offers && offers.stickyOffer;
    const priceGuarantee = offers && offers.propertyInfo && offers.propertyInfo.priceGuarantee;
    const similarHotels = currentHotel && currentHotel.similarHotels;
    const similarHotelsDateless = currentHotel && currentHotel.similarHotelsDateless;
    const destination = searchCriteria && searchCriteria.destination;
    const infositeContext = offers && offers.infositeContext;
    const backToHsrUrl = infositeContext && infositeContext.backToHsrUrl;
    const membershipMessagingCardViewModel = currentHotel && currentHotel.messagingCardModel && currentHotel.messagingCardModel.model;
    const freeLxMessagingCardViewModel =
      currentHotel && currentHotel.offers && currentHotel.offers.freeLxMessagingCard && currentHotel.offers.freeLxMessagingCard.model;
    const { roomsAndRatesTarget } = this.state;
    const rewardsEducation =
      currentHotel &&
      currentHotel.offers &&
      currentHotel.offers.rewardsDialogContent &&
      currentHotel.offers.rewardsDialogContent.rewardsEducation;
    const hrefNextPage = page && page.hrefNextPage;
    const questionAnswersViewModel = currentHotel.hotel && currentHotel.hotel.questionAnswersViewModel;
    const { reviews } = currentHotel;
    const feedbackModel = currentHotel && currentHotel.hotel && currentHotel.hotel.feedbackModel;
    const vrInquiryChatbotViewModel = currentHotel.hotel && currentHotel.hotel.vrInquiryChatbotViewModel;

    if (!this.props.page.seo) {
      this.props.page.title = `${info && info.hotelName} ${destination ? `(${destination})` : ''}`;
    }
    const hotelId = currentHotel && currentHotel.hotelId;
    const shortlistModel = currentHotel && currentHotel.hotel && currentHotel.hotel.shortlistModel;
    const favoriteButton = shortlistModel &&
      shortlistModel.meta &&
      shortlistModel.meta.hotelId && (
        <FavoriteButton
          shortlistModel={shortlistModel}
          labelSave={l10n.formatText('hotels.infosite.shortlist.saveToList.a11yLabel')}
          labelRemove={l10n.formatText('hotels.infosite.shortlist.removeFromList.a11yLabel')}
          category="hotel"
          onSaveMessage={l10n.formatText('hotels.shortlist.saveToList.notification', info.hotelName)}
          id={`favorite-${hotelId}`}
          {...info}
        />
      );
    const masterProps = {
      className: 'infosite',
      dataStid: 'infosite-page-container',
      loading: !this.omnitureData,
      omnitureData: this.omnitureData,
      pageUsable: true,
      route: route,
      searchCriteria: currentHotel.searchCriteria,
      universalDataObject: this.universalDataObject,
      toolbarType: MasterPageToolbarTypes.TOOLBAR_WHITE_BACKGROUND
    };
    const isDateless = searchCriteria && searchCriteria.isDateless;

    const PropertySummaryComponent = (
      <PropertySummary
        {...info}
        backToHsrUrl={backToHsrUrl}
        name={toolbar.name}
        goBack={this.goBack}
        favoriteButton={favoriteButton}
        currentHotel={currentHotel}
        roomsAndRatesTarget={roomsAndRatesTarget}
        stickyOffer={stickyOffer}
      />
    );

    const bentoLayoutName = routeStore && routeStore.queryParams && routeStore.queryParams.sduiPerfTest ? 'pdp-main-layout' : 'pdp-main';
    const structuredData = context && context.viewModelResponse && context.viewModelResponse.structuredData;
    const propertyFaqs = currentHotel.hotel && currentHotel.hotel.propertyFaqs;
    const showReviews = reviews && reviews.count;

    return (
      <MasterPageWrapper {...masterProps}>
        <NavigationRegion className="navigation-bar-region constrained">
          <BentoBox name="pdp-navigation">
            <BentoPortion name="pdp-property-summary">{PropertySummaryComponent}</BentoPortion>
            <BentoPortion name="pdp-navbar">
              {info && info.navigationBarTabList && <NavigationBar navigationBarTabList={info.navigationBarTabList} />}
            </BentoPortion>
          </BentoBox>
        </NavigationRegion>
        <MainRegion key="main-region" className={`main-region infosite__main`}>
          {isFailure && (
            <p className="error">
              <LocalizedText message="hotels.infosite.errorMessage.text" />
              <Link to={'/Hotels'}>
                <LocalizedText message="hotels.infosite.tryAgain.text" />
              </Link>
            </p>
          )}
          <div ref={NavigationRefs.Overview} className="infosite__content infosite__content--details" data-stid="bento_infosite">
            <BentoBox name={bentoLayoutName}>
              <BentoPortion name="pdp-property-details">
                <PropertyDetails
                  {...info}
                  goBack={this.goBack}
                  index={0}
                  priceGuarantee={priceGuarantee}
                  roomsAndRatesTarget={roomsAndRatesTarget}
                  stickyOffer={stickyOffer}
                  favoriteButton={favoriteButton}
                />
              </BentoPortion>
              <BentoSideDish>
                <BentoPortion name="pdp-property-details-ronin">
                  <RoninPropertyDetails
                    {...info}
                    goBack={this.goBack}
                    index={0}
                    priceGuarantee={priceGuarantee}
                    roomsAndRatesTarget={roomsAndRatesTarget}
                    stickyOffer={stickyOffer}
                    favoriteButton={favoriteButton}
                  />
                </BentoPortion>
              </BentoSideDish>

              <BentoPortion name="pdp-mod-banner">
                {currentHotel.messagingCardModel && (
                  <ErrorBoundary label="hotel.information.membership">
                    <div className="mod-signup-signin-banner">
                      <MessagingCard
                        className="all-b-padding-three all-x-padding-six xl-x-padding-zero m-x-padding-zero s-x-padding-two"
                        model={membershipMessagingCardViewModel}
                        routeStore={routeStore}
                        tag="section"
                      />
                    </div>
                  </ErrorBoundary>
                )}
              </BentoPortion>

              <BentoPortion name="pdp-lx-banner">
                {freeLxMessagingCardViewModel && (
                  <ErrorBoundary label="hotel.information.freeLx">
                    <div id="free-lx-banner">
                      <MessagingCard
                        className="all-b-padding-three all-x-padding-six xl-x-padding-zero m-x-padding-zero s-x-padding-two"
                        model={freeLxMessagingCardViewModel}
                        routeStore={routeStore}
                        tag="section"
                      />
                    </div>
                  </ErrorBoundary>
                )}
              </BentoPortion>

              <BentoPortion name="pdp-rooms-and-rates">
                {React.createElement(roomsAndRatesLoadable.codeSplit, {
                  ...offers,
                  forceHistoryPushOnGoBack: this.forceHistoryPushOnGoBack,
                  infoModel: info,
                  loadingOffers,
                  onRoomsAndRatesTargetAvailable: this.setRoomsAndRatesTarget
                })}
              </BentoPortion>

              <BentoPortion name="pdp-sleeping-arrangements">
                <SleepingArrangementsModule analytics={analytics} l10n={l10n} offers={offers} />
              </BentoPortion>

              <BentoPortion name="pdp-chatbot">
                {vrInquiryChatbotViewModel && (
                  <ErrorBoundary label="hotel.information.chatbot">
                    <ChatBot model={vrInquiryChatbotViewModel} />
                  </ErrorBoundary>
                )}
              </BentoPortion>

              <BentoPortion name="pdp-similar-hotels">
                <ErrorBoundary label="hotel.similarHotels">
                  <SimilarHotels similarHotels={similarHotels} analytics={analytics} />
                </ErrorBoundary>
              </BentoPortion>

              <BentoSideDish>
                <BentoPortion name="pdp-map-access">{info && <MapAccess mapModel={info.mapModel} />}</BentoPortion>
              </BentoSideDish>

              <BentoPortion name="pdp-whats-around">
                <ErrorBoundary label="hotel.information.whatsAround_1">
                  <>{info && <WhatsAroundCard mapModel={info.mapModel} />}</>
                </ErrorBoundary>
              </BentoPortion>

              <BentoPortion name="pdp-similar-hotels-dateless">
                <ErrorBoundary label="hotel.information.similarPropertiesDateless">
                  <SimilarHotels similarHotels={similarHotelsDateless} analytics={analytics} isDatelessPdp={isDateless} />
                </ErrorBoundary>
              </BentoPortion>

              <BentoSideDish>
                <BentoPortion name="pdp-photo-gallery">
                  {info &&
                    info.gallery && (
                      <GalleryByCategories
                        currentHotel={currentHotel}
                        gallery={info.gallery}
                        trackingRfrr="PhotoGalleryTab"
                        id={info.hotelId}
                      />
                    )}
                </BentoPortion>
              </BentoSideDish>

              <BentoPortion name="pdp-activities">
                <ErrorBoundary label="hotel.information.activities">
                  <>
                    {this.state.contextInput &&
                      hotelRegion &&
                      hotelRegion.id &&
                      searchData &&
                      searchData.startDate &&
                      searchData.endDate && (
                        <Lazy>
                          <Lazy.Placeholder>
                            <div data-stid="recommended-activities__placeholder" />
                          </Lazy.Placeholder>
                          <Lazy.Loaded>
                            <AbacusExperiment name="LS_PWA_RECOMMENDED_ACTIVITIES">
                              <AbacusControl />
                              <AbacusVariant bucket={1}>
                                <ActivityRecommendations
                                  inputs={{
                                    context: this.state.contextInput,
                                    region: {
                                      regionId: String(hotelRegion.id)
                                    },
                                    dateRange: {
                                      startDate: DateUtils.parseDateString(searchData.startDate),
                                      endDate: DateUtils.parseDateString(searchData.endDate)
                                    }
                                  }}
                                  skipSsr={true}
                                />
                              </AbacusVariant>
                            </AbacusExperiment>
                          </Lazy.Loaded>
                        </Lazy>
                      )}
                  </>
                </ErrorBoundary>
              </BentoPortion>

              <BentoPortion name="pdp-policies">
                <PropertyInformation
                  {...info}
                  {...offers}
                  analytics={this.props.analytics}
                  hrefNextPage={hrefNextPage}
                  questionAnswersViewModel={questionAnswersViewModel}
                />
              </BentoPortion>
              <BentoPortion name="pdp-faqs">
                <DatelessFaqs isDateless={isDateless} propertyFaqs={propertyFaqs} />
              </BentoPortion>
              <BentoPortion name="pdp-user-reviews">
                {showReviews ? <ReviewsOnInfosite currentHotel={currentHotel} /> : <NoReviews />}
              </BentoPortion>
              <BentoPortion name="pdp-feedback">
                {feedbackModel && (
                  <ErrorBoundary label="hotel.information.feedback">
                    <FeedbackButton model={feedbackModel} />
                  </ErrorBoundary>
                )}
              </BentoPortion>
            </BentoBox>
          </div>
          <MesoFooter pageName={PageName.INFOSITE} destination={destination} isDateless={isDateless} />
          <MesoIntentMediaExp pageName={PageName.INFOSITE} searchCriteria={searchCriteria} />
          <QueryRoute query={{ pwaDialog: 'rewardsEducation' }}>
            <RewardsEducationDialog rewardsEducation={rewardsEducation} />
          </QueryRoute>
          <WelcomeNotification />
          <SchemaOrgScript structuredData={structuredData} />
          {multiItem && !multiItem.isPackageShoppingPath() && <MultiItem analytics={analytics} pageName={PageName.INFOSITE} />}
        </MainRegion>
        <SideRailRegion key="side-rail-region">
          <MesoSideRail pageName={PageName.INFOSITE} />
        </SideRailRegion>
      </MasterPageWrapper>
    );
  }

  render() {
    return (
      <AbacusExperiment name="PWA_SDUI_TABBED_LAYOUT">
        <AbacusControl>{this.renderLayout()}</AbacusControl>
        <AbacusVariant bucket={1}>{this.renderLayoutWithSdui()}</AbacusVariant>
      </AbacusExperiment>
    );
  }
}

module.exports = Infosite;

const MasterPageWrapper = ({ children, ...masterProps }) => (
  <Viewport>
    <ViewSmall>
      <AbacusExperiment name="PWA_PDP_CLIENT_ANALYTICS">
        <AbacusControl>
          <MasterPageWrapped
            {...masterProps}
            pageHeaderClassName="header--with-overlay-toolbar"
            toolbarType={masterProps.toolbarType || MasterPageToolbarTypes.TRANSPARENT_OVERLAY}
          >
            {children}
          </MasterPageWrapped>
        </AbacusControl>
        <AbacusVariant bucket={1}>
          <MasterPage
            {...masterProps}
            pageHeaderClassName="header--with-overlay-toolbar"
            toolbarType={masterProps.toolbarType || MasterPageToolbarTypes.TRANSPARENT_OVERLAY}
          >
            {children}
          </MasterPage>
        </AbacusVariant>
      </AbacusExperiment>
    </ViewSmall>
    <ViewLarge>
      <AbacusExperiment name="PWA_PDP_CLIENT_ANALYTICS">
        <AbacusControl>
          <MasterPageWrapped
            {...masterProps}
            toolbarType={MasterPageToolbarTypes.NORMAL}
            sideRailType={
              masterProps.searchCriteria && masterProps.searchCriteria.isDateless ? '' : MasterPageSideRailTypes.NEXT_TO_MAIN_AND_TOOLBAR
            }
          >
            {children}
          </MasterPageWrapped>
        </AbacusControl>
        <AbacusVariant bucket={1}>
          <MasterPage
            {...masterProps}
            toolbarType={MasterPageToolbarTypes.NORMAL}
            sideRailType={
              masterProps.searchCriteria && masterProps.searchCriteria.isDateless ? '' : MasterPageSideRailTypes.NEXT_TO_MAIN_AND_TOOLBAR
            }
          >
            {children}
          </MasterPage>
        </AbacusVariant>
      </AbacusExperiment>
    </ViewLarge>
  </Viewport>
);

const SleepingArrangementsModule = ({ analytics, l10n, offers }) => {
  const sleepingArrangementsViewModel = offers && offers.sleepingArrangementsViewModel;

  if (!sleepingArrangementsViewModel) {
    return null;
  }

  return (
    <ErrorBoundary label="hotel.information.sleepingArrangements">
      <Lazy>
        <Lazy.Placeholder>
          <div data-stid="sleeping-arrangements-placeholder" />
        </Lazy.Placeholder>
        <Lazy.Loaded>
          <SleepingArrangements analytics={analytics} l10n={l10n} sleepingArrangementsViewModel={sleepingArrangementsViewModel} />
        </Lazy.Loaded>
      </Lazy>
    </ErrorBoundary>
  );
};
